import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import type { Notification } from "../types/orderbook";
import { logger } from "../lib/logger";

// Fetch notifications for the current user
async function fetchNotifications(): Promise<Notification[]> {
  const {
    data: { user }
  } = await supabase.auth.getUser();

  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("user_id", user.id)
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) {
    logger.error("Error loading notifications", error);
    throw error;
  }

  return (data || []) as Notification[];
}

// Hook to get notifications
export function useNotifications() {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: fetchNotifications,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Poll every minute for new notifications
    refetchOnWindowFocus: true
  });
}

// Hook to get unread notification count
export function useUnreadNotificationCount() {
  const { data: notifications } = useNotifications();
  return notifications?.filter((n) => !n.is_read).length ?? 0;
}

// Hook to mark a notification as read
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", notificationId);

      if (error) {
        logger.error("Error marking notification as read", error);
        throw error;
      }

      return notificationId;
    },
    onSuccess: (notificationId) => {
      queryClient.setQueryData<Notification[]>(["notifications"], (old) => {
        if (!old) return old;
        return old.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        );
      });
    }
  });
}

// Hook to mark all notifications as read
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const {
        data: { user }
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("Not authenticated");
      }

      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) {
        logger.error("Error marking all notifications as read", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.setQueryData<Notification[]>(["notifications"], (old) => {
        if (!old) return old;
        return old.map((n) => ({ ...n, is_read: true }));
      });
    }
  });
}

// Hook to delete a notification
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId);

      if (error) {
        logger.error("Error deleting notification", error);
        throw error;
      }

      return notificationId;
    },
    onSuccess: (notificationId) => {
      queryClient.setQueryData<Notification[]>(["notifications"], (old) => {
        if (!old) return old;
        return old.filter((n) => n.id !== notificationId);
      });
    }
  });
}

// Hook to clear all notifications
export function useClearAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const {
        data: { user }
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("Not authenticated");
      }

      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("user_id", user.id);

      if (error) {
        logger.error("Error clearing all notifications", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.setQueryData<Notification[]>(["notifications"], () => []);
    }
  });
}
